import React from "react";
import { Stack, TextField, MessageBar, MessageBarType } from "@fluentui/react";
import { useTheme } from "../../../theme/OAS365Theme";
import { useCommonControlsStyle } from "../../../theme/styles/CommonControlsStyle";
import Loader from "../../../components/LoadSpinner";
import FieldValidationMessage from "../../../components/checkinDocuments/FieldValidationMessage";

/**
 * Renders new revision info: error state, loading state, or form (Latest Revision, New Revision, Revision Text).
 * Used by Check-in New Revision flow.
 * @param {string} [latestRevision] - Current/latest revision code (read-only display).
 */
function NewRevisionInfoContent({
  isError,
  errorMessage,
  isLoading,
  latestRevision,
  newRevision,
  onNewRevisionChange,
  newRevisionWarMsg,
  newRevisionText,
  onNewRevisionTextChange,
  showFieldValidation = true,
  newRevisionError = "",
  revisionTextError = "",
}) {
  const theme = useTheme();
  const styleTheme = useCommonControlsStyle();

  if (isError) {
    return (
      <>
        <Stack.Item grow style={{ marginTop: "10px", width: "100%" }}>
          <MessageBar messageBarType={MessageBarType.error}>
            {errorMessage || "Failed to load new revision info."}
          </MessageBar>
        </Stack.Item>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Stack.Item grow style={{ marginTop: "10px", width: "100%" }}>
          <span
            style={{
              fontFamily: theme.fonts.regular,
              fontWeight: theme.fontWeight.menuWeight,
              fontSize: `${theme.fontSizes?.normal ?? 12}px`,
            }}
          >
            Latest Revision
          </span>
          <TextField
            styles={styleTheme.TextFiledsStyle}
            value={latestRevision ?? ""}
            disabled
          />
        </Stack.Item>
        <Stack.Item grow style={{ marginTop: "8px", marginLeft: "0px", width: "100%" }}>
          <Stack tokens={{ childrenGap: 8 }} horizontalAlign="start" verticalAlign="center">
            <Loader />
            <span
              style={{
                fontFamily: theme.fonts.regular,
                fontSize: theme.fontSizes.normal,
                color: theme.colors?.fontColor || "#605e5c",
              }}
            >
              Loading revision info…
            </span>
          </Stack>
        </Stack.Item>
      </>
    );
  }

  return (
    <>
      <Stack.Item grow style={{ marginTop: "10px", width: "100%" }}>
        <span
          style={{
            fontFamily: theme.fonts.regular,
            fontWeight: theme.fontWeight.menuWeight,
            fontSize: `${theme.fontSizes?.normal ?? 12}px`,
          }}
        >
          Latest Revision
          <span style={{ color: theme.colors.fontColor }}> *</span>
        </span>
        <TextField
          styles={styleTheme.TextFiledsStyle}
          value={latestRevision ?? ""}
          disabled
        />
      </Stack.Item>

      <Stack.Item grow style={{ marginTop: "10px", marginLeft: "0px", width: "100%" }}>
        <span
          style={{
            fontFamily: theme.fonts.regular,
            fontSize: theme.fontSizes.normal,
          }}
        >
          New Revision
          <span style={{ color: "red", fontWeight: "bold" }}> *</span>
        </span>
        <TextField
          styles={styleTheme.TextFiledsStyle}
          value={newRevision}
          onChange={onNewRevisionChange}
          disabled={false}
          maxLength={6}
        />
        <FieldValidationMessage
          message={newRevisionWarMsg || (showFieldValidation ? newRevisionError : "")}
        />
      </Stack.Item>

      <Stack.Item grow style={{ marginTop: "10px", width: "100%" }}>
        <span
          style={{
            fontFamily: theme.fonts.regular,
            fontSize: theme.fontSizes.normal,
          }}
        >
          Revision Text
          <span style={{ color: "red", fontWeight: "bold" }}> *</span>
        </span>
      </Stack.Item>
      <Stack.Item grow align="center" style={{ marginLeft: "0px", width: "100%" }}>
        <TextField
          styles={{
            ...styleTheme.TextFiledMultiLineStyle,
            field: {
              ...(styleTheme.TextFiledMultiLineStyle?.field ?? {}),
              fontFamily: theme.fonts.regular,
              fontSize: `${theme.fontSizes?.normal ?? 12}px`,
            },
          }}
          value={newRevisionText}
          multiline
          rows={7}
          onChange={(ev, item) => onNewRevisionTextChange(item)}
        />
        <FieldValidationMessage
          message={showFieldValidation ? revisionTextError : ""}
        />
      </Stack.Item>
    </>
  );
}

export default NewRevisionInfoContent;
